import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { cn } from '../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger } from './ui/select';

const languageOptions = [
  { value: 'de', label: 'DE', name: 'Deutsch' },
  { value: 'en', label: 'EN', name: 'English' },
  { value: 'tr', label: 'TR', name: 'Türkçe' },
  { value: 'ru', label: 'RU', name: 'Русский' },
  { value: 'hr', label: 'HR', name: 'Hrvatski' },
  { value: 'it', label: 'IT', name: 'Italiano' }
] as const;

type LanguageOption = (typeof languageOptions)[number];

const orderedLanguageOptions: LanguageOption[] = [
  languageOptions.find((option) => option.value === 'de')!,
  ...languageOptions.filter((option) => option.value !== 'de')
];

type LanguageSwitcherProps = {
  fullWidth?: boolean;
};

export const LanguageSwitcher = ({ fullWidth = false }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentLang = mounted ? i18n.resolvedLanguage ?? i18n.language : 'de';
  const currentOption = useMemo(
    () => orderedLanguageOptions.find((option) => option.value === currentLang) ?? orderedLanguageOptions[0],
    [currentLang]
  );

  useEffect(() => {
    if (mounted) {
      document.documentElement.lang = currentLang;
    }
  }, [currentLang, mounted]);
  const triggerPrimary = fullWidth ? currentOption.name : currentOption.label;
  const triggerAriaLabel = `Sprache wählen (aktuell ${currentOption.name})`;

  return (
    <Select
      value={currentLang}
      onValueChange={(value) => {
        i18n.changeLanguage(value);
      }}
    >
      <SelectTrigger
        className={cn(
          'h-10 overflow-hidden rounded-full border border-slate-200 bg-white/80 px-3 text-sm font-medium text-slate-700 shadow-sm backdrop-blur transition-colors hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:hover:border-slate-500',
          fullWidth ? 'w-full justify-between' : 'w-[92px] justify-between'
        )}
        aria-label={triggerAriaLabel}
      >
        <span className="inline-flex items-center gap-2">
          <Languages className="h-4 w-4 text-slate-500 dark:text-slate-300" />
          <span
            className={cn(
              'truncate font-semibold text-slate-800 dark:text-slate-100',
              fullWidth ? 'text-sm' : 'text-xs'
            )}
          >
            {triggerPrimary}
          </span>
        </span>
      </SelectTrigger>
      <SelectContent
        align="end"
        sideOffset={8}
        className="z-[1200] min-w-[180px] rounded-2xl border border-slate-200 bg-white/95 p-1 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/95"
      >
        {orderedLanguageOptions.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className="rounded-xl py-2 pl-8 pr-3 text-sm font-medium text-slate-700 focus:bg-slate-100 dark:text-slate-100 dark:focus:bg-slate-800"
          >
            <span className="flex w-full items-center justify-between gap-3">
              <span>{option.name}</span>
              <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                {option.label}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
